import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import md5 from 'blueimp-md5';
import Gravatar from 'react-gravatar';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import { withTheme } from 'common/styling/theme';
import {
  PageContentPreloader,
  PageLayout,
  RadioButton,
  DateRangeField,
  DefaultButton,
  SearchPanel,
  BaseSelectField,
  PaginationTable,
  NoItemsPanel,
  BasicModal,
  NewDefaultButton,
} from 'common/components';
import noPaymentsImage from 'common/images/noPayments.svg';
import { createInvestNewExports } from 'common/_redux';

import InvestCRMWithdrawlForm from '../../_components/InvestCRMWithdrawlForm';

import { boGetInvestCRMStrategyInvestors } from '../../_redux';

import { staticStyles, getDynamicStyles } from './style';

const yearsOptions = [...Array(10).keys()].map(i => ({
  value: moment().year() - i,
  label: moment().year() - i,
}));

const InvestStrategyPageNew = ({
  investCRMStrategyInvestors,
  investCRMStrategyInvestorsAreLoaded,
  manageInvestExport,
  getInvestStrategyInvestors,
  createInvestNewExports,
  investFullAccessPerm,
  theme,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [exportExt, setExportExt] = useState(null);
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedInvestmentId, setSelectedInvestmentId] = useState(null);
  const toggleWithdrawalModal = () => setIsWithdrawalModalOpen(!isWithdrawalModalOpen);
  const {
    params: { strategy },
  } = match;
  const query = useMemo(() => ({ page_size: 10, ...queryString.parse(location.search) }), [location.search]);

  const getList = useCallback(
    query => {
      getInvestStrategyInvestors({ strategyId: strategy, ...query });
    },
    [getInvestStrategyInvestors]
  );

  useEffect(() => {
    getList({ strategyId: strategy, search: query });
  }, [getList]);

  const openInvestorAccount = accountId => history.push(`/backoffice/users/${accountId}`);

  /* eslint-disable */
  const columns = useMemo(
    () => [
      {
        accessor: 'investor__first_name',
        Header: 'boInvestFullName',
        Cell: ({
          row: {
            original: { userName, userEmail, investorId },
          },
        }) => (
          <div className="InvestStrategyTable__basic-info" onClick={() => openInvestorAccount(investorId)}>
            <Gravatar md5={md5(userEmail)} size={33} />
            <span className="user-name">{userName}</span>
          </div>
        ),
      },
      {
        accessor: 'investor__email',
        Header: 'boInvestEmail',
        Cell: ({
          row: {
            original: { userEmail },
          },
        }) => <span className="InvestStrategyTable__email">{userEmail}</span>,
      },
      {
        accessor: 'equity',
        Header: 'boInvestEquity',
        Cell: ({
          row: {
            original: { equity },
          },
        }) => <span className="InvestStrategyTable__equity">{`${equity.amount} ${equity.currency ?? ''}`}</span>,
      },
      {
        Header: 'boInvestTotalProfit',
        Cell: ({
          row: {
            original: { totalProfit },
          },
        }) => (
          <span className="InvestStrategyTable__total-profit">{`${totalProfit.amount} ${
            totalProfit.currency ?? ''
          }`}</span>
        ),
      },
      {
        Header: 'boInvestRewardAccount',
        Cell: ({
          row: {
            original: { masterAccount },
          },
        }) => <span className="InvestStrategyTable__reward-account">{masterAccount}</span>,
      },
      ...(manageInvestExport
        ? [
            {
              Header: 'justExport',
              Cell: ({
                row: {
                  original: { id },
                },
              }) => {
                return (
                  <div className="InvestStrategiesTable__cell InvestStrategiesTable__cell-font--medium  InvestStrategiesTable__cell--status">
                    <NewDefaultButton
                      textId="justExport"
                      size="small"
                      variant="secondary"
                      onClick={e => {
                        setIsExportMenuModalOpen(true);
                        setSelectedInvestmentId(id);
                        setExportExt('xlsx');
                      }}
                    />
                  </div>
                );
              },
            },
          ]
        : []),
      {
        Header: 'boInvestStrategyActivity',
        Cell: ({
          row: {
            original: { id, enabled, investorId },
          },
        }) => {
          return (
            <div className="InvestStrategyTable__status_cell">
              {!!enabled && (
                <NewDefaultButton
                  textId="justDisable"
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    setSelectedUserId(investorId);
                    setSelectedInvestmentId(id);
                    toggleWithdrawalModal();
                  }}
                />
              )}
              <div className={`status ${!!enabled ? 'active' : ''}`} />
            </div>
          );
        },
      },
    ],
    [query]
  );
  /* eslint-enable */
  const [isExportMenuModalOpen, setIsExportMenuModalOpen] = useState(false);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState();

  const [isCreateFullExport, setIsCreateFullExport] = useState(true);

  useEffect(() => {
    if (isExportMenuModalOpen) {
      setSelectedTimePeriod(
        `${moment(moment().add(-7, 'day').startOf('day')).format('YYYY-MM-DD')}_${moment(
          moment().startOf('day')
        ).format('YYYY-MM-DD')}`
      );
    }
  }, [isExportMenuModalOpen]);

  return (
    <PageLayout
      captionSlug="boInvestStrategyTitle"
      captionValues={{ strategy: investCRMStrategyInvestors.strategy }}
      withBackButton>
      <PageContentPreloader ready={investCRMStrategyInvestorsAreLoaded} type="bigLine" firstLaunchOnly>
        <SearchPanel
          location={location}
          loading={!investCRMStrategyInvestorsAreLoaded}
          history={history}
          getList={getList}
          params={{ strategyName: strategy, search: query }}
          searchTextId="boInvestSearchByEmail"
        />
        {manageInvestExport && (
          <div className="InvestStrategyPageNew__Exports">
            <BaseSelectField
              name="import"
              textId="justInvestCrmExports"
              options={[
                { value: 'showExports', label: 'Show exports' },
                { value: 'xlsx', label: 'Export in xlsx' },
                { value: 'csv', label: 'Export in csv' },
              ]}
              onChange={option => {
                if (option.value === 'showExports') {
                  history.push('/backoffice/invest_crm/exports');
                } else {
                  setExportExt(option.value);
                  setSelectedInvestmentId(null);
                  setIsExportMenuModalOpen(true);
                }
              }}
              maxMenuHeight={170}
            />
          </div>
        )}
        {investCRMStrategyInvestors.count ? (
          <div className={classNames('InvestStrategyTable', { full: investFullAccessPerm })}>
            <PaginationTable
              data={investCRMStrategyInvestors}
              columns={columns}
              loading={!investCRMStrategyInvestorsAreLoaded}
              getList={getList}
              location={location}
              history={history}
              params={{ strategyId: strategy, search: query }}
              noResponsive
            />
          </div>
        ) : (
          <NoItemsPanel icon={noPaymentsImage} captionSlug="noStrategiesMatchingTheParameters" />
        )}
      </PageContentPreloader>
      <BasicModal
        isOpen={isWithdrawalModalOpen}
        captionId="boCloseInvestmentInStrategy"
        onRequestClose={toggleWithdrawalModal}>
        <InvestCRMWithdrawlForm
          userId={selectedUserId}
          strategyId={strategy}
          search={query}
          investmentId={selectedInvestmentId}
          toggleWithdrawalModal={toggleWithdrawalModal}
        />
      </BasicModal>
      <BasicModal
        captionId="selectTimePeriodOfExport"
        isOpen={isExportMenuModalOpen}
        onRequestClose={() => setIsExportMenuModalOpen(false)}>
        <div className="UserPageExports">
          <RadioButton
            name="investFrom"
            textId="createExportForAllTime"
            value="all"
            onChange={() => setIsCreateFullExport(true)}
            checked={isCreateFullExport}
          />

          <RadioButton
            name="investFrom"
            textId="createExportForTimePeriod"
            value="all"
            onChange={() => setIsCreateFullExport(false)}
            checked={!isCreateFullExport}
          />
          <div style={{ width: 'max-content', display: !isCreateFullExport ? 'flex' : 'none' }}>
            <DateRangeField
              yearsOptions={yearsOptions}
              input={{ value: selectedTimePeriod, onChange: setSelectedTimePeriod }}
              meta={{ touched: true, error: false }}
              noRedux
              relativeInner
              daysLimit={30}
            />
          </div>

          <DefaultButton
            textId="justExport"
            type="button"
            filled
            disabled={!selectedTimePeriod && !isCreateFullExport}
            onClick={() => {
              setIsExportMenuModalOpen(false);
              createInvestNewExports({
                timePeriod: isCreateFullExport ? undefined : selectedTimePeriod,
                investmentId: selectedInvestmentId,
                ext: exportExt,
              });
            }}
          />
        </div>
      </BasicModal>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </PageLayout>
  );
};

InvestStrategyPageNew.propTypes = {
  investCRMStrategyInvestors: PropTypes.object.isRequired,
  investCRMStrategyInvestorsAreLoaded: PropTypes.bool.isRequired,
  getInvestStrategyInvestors: PropTypes.func.isRequired,
  investFullAccessPerm: PropTypes.bool,
  manageInvestExport: PropTypes.bool.isRequired,
  createInvestNewExports: PropTypes.func.isRequired,
  theme: PropTypes.object,
};

InvestStrategyPageNew.defaultProps = {
  investFullAccessPerm: false,
  theme: {},
};

const ThemedInvestInvestStrategyPage = withTheme()(
  connect(
    state => ({
      investCRMStrategyInvestors: state.backoffice.investCRM.investCRMStrategyInvestors,
      investCRMStrategyInvestorsAreLoaded: state.backoffice.investCRM.investCRMStrategyInvestorsAreLoaded,
      investFullAccessPerm:
        state.user.profile.perms.investFullAccessPerm || state.user.profile.perms.managerFullAccessPerm,
      manageInvestExport:
        state.user.profile.perms.manageInvestExport ||
        state.user.profile.perms.investFullAccessPerm ||
        state.user.profile.perms.managerFullAccessPerm,
    }),
    {
      createInvestNewExports: ({ timePeriod, investmentId, strategyId, ext }) =>
        createInvestNewExports.request({ timePeriod, investmentId, strategyId, ext }),
      getInvestStrategyInvestors: ({ strategyId, search }) =>
        boGetInvestCRMStrategyInvestors.request(strategyId, search),
    }
  )(InvestStrategyPageNew)
);

export default ThemedInvestInvestStrategyPage;
export { InvestStrategyPageNew };
